import type { MapStand } from "../shared/types";
import {
  venueMapBoothLabelFrame,
  venueMapBoothNames,
  venueMapBoothTextBounds,
  type VenueMapBoothTextRun,
  type VenueMapBoothTextLayout,
} from "./venueMapBoothLabelLayout";

export type VenueMapLogoTextRun = VenueMapBoothTextRun;
export interface VenueMapLogoLayout extends VenueMapBoothTextLayout {
  logo: { x: number; y: number; width: number; height: number };
}

const layouts = new WeakMap<MapStand, VenueMapLogoLayout | undefined>();

/** Logo modes share the ordinary label's corner number and safe roof geometry. */
export function venueMapLogoLayout(
  stand: MapStand,
): VenueMapLogoLayout | undefined {
  if (layouts.has(stand)) return layouts.get(stand);
  const layout = layoutBoothLogo(stand);
  layouts.set(stand, layout);
  return layout;
}

function layoutBoothLogo(stand: MapStand): VenueMapLogoLayout | undefined {
  if (
    !stand.logo ||
    (stand.labelMode !== "logo" && stand.labelMode !== "logo-text")
  )
    return undefined;
  if (
    !Number.isFinite(stand.logo.width) ||
    !Number.isFinite(stand.logo.height) ||
    stand.logo.width <= 0 ||
    stand.logo.height <= 0
  )
    return undefined;
  const frame = venueMapBoothLabelFrame(stand);
  if (!frame) return undefined;
  const bodyHeight = frame.bodyBottom - frame.bodyTop;
  if (bodyHeight <= 0 || frame.width <= 0) return undefined;
  const names =
    stand.labelMode === "logo-text"
      ? venueMapBoothNames(stand, frame.width, bodyHeight * 0.32)
      : { lines: [], size: 0, height: 0 };
  const gap = names.lines.length ? frame.gap : 0;
  const imageSpace = bodyHeight - names.height - gap;
  const imageScale = Math.min(
    frame.width / stand.logo.width,
    imageSpace / stand.logo.height,
  );
  const logoWidth = stand.logo.width * imageScale;
  const logoHeight = stand.logo.height * imageScale;
  const contentHeight = logoHeight + gap + names.height;
  const top = (frame.bodyTop + frame.bodyBottom - contentHeight) / 2;
  const logo = {
    x: frame.centreX - logoWidth / 2,
    y: top,
    width: logoWidth,
    height: logoHeight,
  };
  const text: VenueMapBoothTextRun[] = [
    ...(stand.label.showId === false ? [] : [frame.id]),
    ...names.lines.map((name, index): VenueMapBoothTextRun => ({
      text: name,
      x: frame.centreX,
      y: logo.y + logo.height + gap + names.size + index * names.size * 1.3,
      size: names.size,
      weight: 600,
      align: "center",
    })),
  ];
  const textBounds = venueMapBoothTextBounds(text);
  return {
    logo,
    text,
    bounds: {
      minX: Math.min(textBounds.minX, logo.x),
      minY: Math.min(textBounds.minY, logo.y),
      maxX: Math.max(textBounds.maxX, logo.x + logo.width),
      maxY: Math.max(textBounds.maxY, logo.y + logo.height),
    },
  };
}
